import * as React from 'react';

import { VirtualKeyboardContext } from './VirtualKeyboardContext';

export type VirtualFullKeyboardProps = {
  children: React.ReactNode;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
};

export const VirtualKeyboardWrapper = (props: VirtualFullKeyboardProps) => {
  const { setValue, showKeyboard, hideKeyboard, setChangeListener } =
    React.useContext(VirtualKeyboardContext);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const handleFocusIn = React.useCallback(() => {
    if (props.disabled) {
      return;
    }

    if (props.onChange) {
      setChangeListener(props.onChange);
    }

    setValue(props.value ?? '');
    showKeyboard();

    setTimeout(() => {
      try {
        (document.activeElement as HTMLInputElement).setSelectionRange(
          Number.MAX_SAFE_INTEGER - 1,
          Number.MAX_SAFE_INTEGER,
        );
        document.activeElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
        setValue(props.value ?? '');
      } catch {
        // ignore
      }
    }, 100);
  }, [
    props.disabled,
    props.onChange,
    props.value,
    setChangeListener,
    setValue,
    showKeyboard,
  ]);

  /*
   * Open keyboard when focused
   */
  React.useLayoutEffect(() => {
    const focusOutListener = () => {
      if (!wrapperRef.current?.contains(document.activeElement)) {
        hideKeyboard();
      }
    };

    wrapperRef.current?.addEventListener('focusin', handleFocusIn);
    wrapperRef.current?.addEventListener('focusout', focusOutListener);

    return () => {
      wrapperRef.current?.removeEventListener('focusin', handleFocusIn);

      wrapperRef.current?.removeEventListener('focusout', focusOutListener);
    };
  }, [handleFocusIn, hideKeyboard]);

  React.useEffect(() => {
    setValue(props.value ?? '');
  }, [props.value, setValue]);

  return (
    <div ref={wrapperRef} className="@VirtualKeyboardWrapper contents">
      {React.Children.map(props.children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(
              child as React.ReactElement<VirtualFullKeyboardProps>,
              {
                onChange: props.onChange,
                value: props.value,
              },
            )
          : child,
      )}
    </div>
  );
};
